import { Controller } from "@hotwired/stimulus"
import { initSearchableSelect } from "../components/searchable_select"

// Modal + wizard de création d'étude — 4 étapes :
//   0. Identité (ref, nom, provenance, confidentialité)
//   1. Cadrage (prestation, statut)
//   2. Équipe (responsable + 3 chargés)
//   3. Récapitulatif → submit
//
// `date_debut` est posée à aujourd'hui via un hidden_field côté serveur, et
// `date_signature` se saisit depuis la fiche étude. Pas de validation de date
// côté client.
//
// La modale se ferme via la croix, ESC, ou clic sur le backdrop. En mode
// turbo-frame (ouverte depuis l'index), on vide la frame ; sinon on retombe
// sur l'URL fallback (l'index des études).
export default class extends Controller {
  static targets = [
    "form",
    "step",
    "stepDot",
    "nextBtn",
    "prevBtn",
    "submitBtn",
    "progressCurrent",
    "progressTotal",
    "error",
    // Étape 1
    "refEtude",
    "etudeName",
    "provenance",
    "confidentielle",
    // Étape 2
    "prestation",
    "statut",
    // Étape 3
    "respEtude",
    "chargeEtude",
    "chargeQualite",
    "chargeRh",
    // Récap
    "recapRef",
    "recapName",
    "recapProvenance",
    "recapConfidentielle",
    "recapPrestation",
    "recapStatut",
    "recapRespEtude",
    "recapChargeEtude",
    "recapChargeQualite",
    "recapChargeRh",
  ]

  static values = {
    current:     { type: Number, default: 0 },
    total:       { type: Number, default: 4 },
    fallbackUrl: { type: String, default: "" },
  }

  connect() {
    if (this.hasProgressTotalTarget) {
      this.progressTotalTarget.textContent = this.totalValue
    }
    // Empêche le scroll de la page derrière la modale.
    this._previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    this.render()

    // Quand la modale est chargée via turbo-frame, l'event `turbo:load` n'a
    // pas lieu — on initialise manuellement Select2 sur les selects membres.
    try { initSearchableSelect() } catch (_) { /* select2/jquery non chargés */ }
  }

  disconnect() {
    document.body.style.overflow = this._previousOverflow || ""
  }

  // --- Navigation entre étapes ---------------------------------------------

  next(event) {
    event?.preventDefault()
    if (!this.validateStep(this.currentValue)) return
    if (this.currentValue < this.stepTargets.length - 1) {
      this.currentValue += 1
      this.render()
      this.focusFirstField()
    }
  }

  prev(event) {
    event?.preventDefault()
    if (this.currentValue > 0) {
      this.currentValue -= 1
      this.clearError()
      this.render()
    }
  }

  render() {
    this.stepTargets.forEach((panel, idx) => {
      const active = idx === this.currentValue
      panel.classList.toggle("is-active", active)
      panel.setAttribute("aria-hidden", active ? "false" : "true")
    })

    this.stepDotTargets.forEach((dot, idx) => {
      dot.classList.toggle("is-active", idx === this.currentValue)
      dot.classList.toggle("is-done", idx < this.currentValue)
    })

    const isLast = this.currentValue === this.stepTargets.length - 1
    if (this.hasNextBtnTarget) this.nextBtnTarget.hidden = isLast
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.hidden = !isLast
    if (this.hasPrevBtnTarget) this.prevBtnTarget.hidden = this.currentValue === 0
    if (this.hasProgressCurrentTarget) {
      this.progressCurrentTarget.textContent = this.currentValue + 1
    }

    if (isLast) this.renderRecap()
  }

  // --- Fermeture de la modale ----------------------------------------------

  close(event) {
    event?.preventDefault()
    // Si l'URL est /etudes/new, c'est un accès direct : on n'a pas d'index
    // chargé derrière, on navigue vers l'index. Sinon (modale ouverte par
    // dessus l'index via turbo-frame), on se contente de vider la frame —
    // l'index reste en place sans rechargement.
    const onNewPath = /\/etudes\/new\/?$/.test(window.location.pathname)
    const frame = this.element.closest("turbo-frame")

    if (!onNewPath && frame && frame.id === "etude_modal") {
      frame.innerHTML = ""
      return
    }

    if (this.fallbackUrlValue) {
      if (window.Turbo && typeof window.Turbo.visit === "function") {
        window.Turbo.visit(this.fallbackUrlValue)
      } else {
        window.location.href = this.fallbackUrlValue
      }
    }
  }

  backdropClose(event) {
    if (event.target === event.currentTarget) this.close(event)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close(event)
  }

  // --- Validation par étape -------------------------------------------------

  validateStep(stepIndex) {
    this.clearError()
    switch (stepIndex) {
      case 0: return this.validateIdentite()
      case 1: return this.validateCadrage()
      case 2: return this.validateEquipe()
      default: return true
    }
  }

  validateIdentite() {
    const ref = this.refEtudeTarget.value.trim()
    if (!ref) return this.showError("Renseignez la référence de l'étude.", this.refEtudeTarget)
    if (!/^\d+$/.test(ref)) return this.showError("La référence doit être un nombre entier.", this.refEtudeTarget)

    const name = this.etudeNameTarget.value.trim()
    if (!name) return this.showError("Donnez un nom court à l'étude (ex : p-bpifrance).", this.etudeNameTarget)

    if (!this.provenanceTarget.value) {
      return this.showError("Précisez la provenance de l'étude.", this.provenanceTarget)
    }

    if (!this.confidentielleTargets.some((r) => r.checked)) {
      return this.showError("Choisissez le niveau de confidentialité.")
    }
    return true
  }

  validateCadrage() {
    if (!this.prestationTarget.value) {
      return this.showError("Choisissez une prestation.", this.prestationTarget)
    }
    if (!this.statutTargets.some((r) => r.checked)) {
      return this.showError("Sélectionnez un statut.")
    }
    return true
  }

  validateEquipe() {
    const roles = [
      [this.respEtudeTarget,     "Sélectionnez le responsable d'étude."],
      [this.chargeEtudeTarget,   "Sélectionnez le chargé d'étude."],
      [this.chargeQualiteTarget, "Sélectionnez le chargé de qualité."],
      [this.chargeRhTarget,      "Sélectionnez le chargé RH."],
    ]
    for (const [field, msg] of roles) {
      if (!field.value) return this.showError(msg, field)
    }
    return true
  }

  // --- Récap ----------------------------------------------------------------

  renderRecap() {
    this.recapRefTarget.textContent          = this.refEtudeTarget.value || "—"
    this.recapNameTarget.textContent         = this.etudeNameTarget.value || "—"
    this.recapProvenanceTarget.textContent   = this.provenanceTarget.value || "—"
    this.recapConfidentielleTarget.textContent = this.confidentielleLabel()

    this.recapPrestationTarget.textContent   = this.selectLabel(this.prestationTarget)
    this.recapStatutTarget.textContent       = this.statutLabel()

    this.recapRespEtudeTarget.textContent     = this.selectLabel(this.respEtudeTarget)
    this.recapChargeEtudeTarget.textContent   = this.selectLabel(this.chargeEtudeTarget)
    this.recapChargeQualiteTarget.textContent = this.selectLabel(this.chargeQualiteTarget)
    this.recapChargeRhTarget.textContent      = this.selectLabel(this.chargeRhTarget)
  }

  selectLabel(select) {
    if (!select || !select.value) return "—"
    const opt = select.selectedOptions[0]
    const txt = opt?.textContent?.trim()
    return txt && txt !== "—" ? txt : "—"
  }

  confidentielleLabel() {
    const checked = this.confidentielleTargets.find((r) => r.checked)
    if (!checked) return "—"
    return checked.value === "true" ? "Confidentielle" : "Non confidentielle"
  }

  statutLabel() {
    const checked = this.statutTargets.find((r) => r.checked)
    return checked ? checked.value : "—"
  }

  // --- Helpers UI -----------------------------------------------------------

  focusFirstField() {
    const step = this.stepTargets[this.currentValue]
    if (!step) return
    const el = step.querySelector("input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled])")
    if (el && typeof el.focus === "function" && !el.classList.contains("searchable-select")) {
      el.focus({ preventScroll: true })
    }
  }

  showError(message, focusEl) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.hidden = false
    }
    if (focusEl && typeof focusEl.focus === "function") {
      focusEl.focus({ preventScroll: true })
    }
    return false
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.hidden = true
    this.errorTarget.textContent = ""
  }
}
